/**
 * LinkedIn Integration OAuth Utilities (Story 10.1, Task 1)
 *
 * Handles LinkedIn OAuth URL building, token exchange, refresh,
 * and error parsing for LinkedIn Pages and LinkedIn Ads.
 *
 * Key differences from Meta OAuth:
 * - No PKCE — LinkedIn uses client_secret (server-side flow)
 * - Space-delimited scopes (not comma-separated like Meta)
 * - Has actual refresh_token (Meta uses long-lived token exchange)
 * - Token exchange uses application/x-www-form-urlencoded (NOT JSON)
 * - Refresh token TTL counts DOWN from initial 365-day grant
 */

import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";
import { ALL_LINKEDIN_SCOPES } from "./scopes";

// ============================================================================
// Constants
// ============================================================================

const LINKEDIN_AUTH_URL = "https://www.linkedin.com/oauth/v2/authorization";
const LINKEDIN_TOKEN_URL = "https://www.linkedin.com/oauth/v2/accessToken";
const TOKEN_EXCHANGE_TIMEOUT_MS = 25_000;

// ============================================================================
// Types
// ============================================================================

export interface LinkedInAuthUrlOptions {
  clientId: string;
  redirectUri: string;
  /** KV state key to embed in state param */
  stateKey: string;
}

export interface LinkedInTokenResponse {
  access_token: string;
  expires_in: number;
  refresh_token: string;
  refresh_token_expires_in: number;
  scope: string;
}

export interface LinkedInCallbackError {
  code: string;
  userMessage: string;
  userAction: "retry" | "none";
}

// ============================================================================
// Build Authorization URL (Task 1.1)
// ============================================================================

/**
 * Builds the LinkedIn OAuth 2.0 authorization URL.
 *
 * Key differences from Meta:
 * - Scopes are space-delimited (URL-encoded as %20)
 * - No auth_type param (no incremental consent concept like Meta)
 * - response_type=code (standard authorization code flow)
 */
export function buildLinkedInAuthUrl(options: LinkedInAuthUrlOptions): string {
  const { clientId, redirectUri, stateKey } = options;

  if (!clientId) {
    throw new Error("LINKEDIN_CLIENT_ID is required");
  }
  if (!redirectUri) {
    throw new Error("LINKEDIN_OAUTH_REDIRECT_URI is required");
  }
  if (!stateKey) {
    throw new Error("stateKey is required for CSRF protection");
  }

  const url = new URL(LINKEDIN_AUTH_URL);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", clientId);
  url.searchParams.set("redirect_uri", redirectUri);
  url.searchParams.set("state", stateKey);
  // LinkedIn uses space-delimited scopes
  url.searchParams.set("scope", ALL_LINKEDIN_SCOPES.join(" "));

  return url.toString();
}

// ============================================================================
// Token Exchange (Task 1.1)
// ============================================================================

/**
 * Exchanges an authorization code for access and refresh tokens.
 *
 * CRITICAL: Content-Type MUST be application/x-www-form-urlencoded.
 * LinkedIn rejects JSON bodies for token exchange.
 *
 * @returns The parsed token response
 * @throws Error if the exchange fails
 */
export async function exchangeLinkedInCode(
  code: string,
  clientId: string,
  clientSecret: string,
  redirectUri: string
): Promise<LinkedInTokenResponse> {
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    code,
    client_id: clientId,
    client_secret: clientSecret,
    redirect_uri: redirectUri,
  });

  const result = await safeFetch(
    LINKEDIN_TOKEN_URL,
    {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    },
    TOKEN_EXCHANGE_TIMEOUT_MS
  );

  if (!result.success) {
    throw new Error(
      sanitizeErrorMessage(`LinkedIn token exchange failed: ${result.error.message}`)
    );
  }

  const response = result.data;
  if (!response.ok) {
    let errorMessage = `LinkedIn token exchange failed: HTTP ${response.status}`;
    try {
      const errorBody = (await response.json()) as {
        error?: string;
        error_description?: string;
      };
      if (errorBody.error_description) {
        errorMessage = `LinkedIn token exchange failed: ${errorBody.error_description}`;
      }
    } catch {
      // ignore parse errors
    }
    throw new Error(sanitizeErrorMessage(errorMessage));
  }

  const data = (await response.json()) as LinkedInTokenResponse;

  if (!data.access_token || !data.expires_in) {
    throw new Error("Invalid LinkedIn token response: missing access_token or expires_in");
  }

  if (!data.refresh_token || !data.refresh_token_expires_in) {
    throw new Error("Invalid LinkedIn token response: missing refresh_token or refresh_token_expires_in");
  }

  return data;
}

// ============================================================================
// Token Refresh (Task 1.1)
// ============================================================================

/**
 * Refreshes a LinkedIn access token using the refresh token.
 *
 * CRITICAL: refresh_token_expires_in counts DOWN from the initial 365-day grant.
 * It does NOT reset on refresh. When the refresh token expires, user MUST re-authorize.
 *
 * @returns The parsed token response with new access_token and updated expiry values
 * @throws Error if the refresh fails
 */
export async function refreshLinkedInToken(
  refreshToken: string,
  clientId: string,
  clientSecret: string
): Promise<LinkedInTokenResponse> {
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    refresh_token: refreshToken,
    client_id: clientId,
    client_secret: clientSecret,
  });

  const result = await safeFetch(
    LINKEDIN_TOKEN_URL,
    {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    },
    TOKEN_EXCHANGE_TIMEOUT_MS
  );

  if (!result.success) {
    throw new Error(
      sanitizeErrorMessage(`LinkedIn token refresh failed: ${result.error.message}`)
    );
  }

  const response = result.data;
  if (!response.ok) {
    let errorMessage = `LinkedIn token refresh failed: HTTP ${response.status}`;
    try {
      const errorBody = (await response.json()) as {
        error?: string;
        error_description?: string;
      };
      if (errorBody.error_description) {
        errorMessage = `LinkedIn token refresh failed: ${errorBody.error_description}`;
      }
    } catch {
      // ignore parse errors
    }
    throw new Error(sanitizeErrorMessage(errorMessage));
  }

  const data = (await response.json()) as LinkedInTokenResponse;

  if (!data.access_token || !data.expires_in) {
    throw new Error("Invalid LinkedIn refresh response: missing access_token or expires_in");
  }

  return data;
}

// ============================================================================
// Error Parsing (Task 1.1)
// ============================================================================

/**
 * Parses LinkedIn OAuth callback errors from query params.
 *
 * LinkedIn returns errors as query params on redirect:
 * ?error=user_cancelled_login&error_description=...&state=...
 *
 * LinkedIn error codes:
 * - user_cancelled_login: User cancelled at login screen
 * - user_cancelled_authorize: User cancelled at consent screen
 */
export function parseLinkedInCallbackError(
  searchParams: URLSearchParams
): LinkedInCallbackError | null {
  const error = searchParams.get("error");
  if (!error) return null;

  // User cancelled login
  if (error === "user_cancelled_login") {
    return {
      code: "oauth-cancelled",
      userMessage: "You cancelled the LinkedIn login.",
      userAction: "retry",
    };
  }

  // User cancelled authorization
  if (error === "user_cancelled_authorize") {
    return {
      code: "oauth-cancelled",
      userMessage: "You cancelled the LinkedIn authorization.",
      userAction: "retry",
    };
  }

  // Access denied
  if (error === "access_denied") {
    return {
      code: "oauth-denied",
      userMessage: "LinkedIn denied the connection request.",
      userAction: "retry",
    };
  }

  // Redirect URI mismatch or invalid request
  if (error === "invalid_request") {
    return {
      code: "oauth-config-error",
      userMessage: "LinkedIn connection is misconfigured. Contact support.",
      userAction: "none",
    };
  }

  // Default / unknown error — never expose raw LinkedIn error_description to user
  return {
    code: "oauth-error",
    userMessage: "An error occurred connecting to LinkedIn. Please try again.",
    userAction: "retry",
  };
}

// Re-exports
export {
  LINKEDIN_INTEGRATION_SCOPES,
  ALL_LINKEDIN_SCOPES,
  LINKEDIN_API_VERSION,
  LINKEDIN_OAUTH_PLATFORMS,
  LINKEDIN_PLATFORM_VALUES,
  getEnabledLinkedInPlatforms,
  detectLinkedInScopeReduction,
  type LinkedInScopeConfig,
  type LinkedInPlatform,
} from "./scopes";
